/**
 * AreaTrend — token-themed area/line trend chart (recharts wrapper).
 * One or more series over a shared X axis, each with a soft gradient fill.
 * Direction: docs/proposals/UX_REFRESH_DIRECTION.md §3 "Data is the hero".
 */
import { useId } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { CHART_COLORS, AXIS_TICK, seriesColor, isDocRtl } from './chartTheme';
import { ChartTooltip } from './ChartTooltip';

export interface AreaTrendSeries {
  /** Key into each data row. */
  key: string;
  /** Legend / tooltip label. Defaults to `key`. */
  name?: string;
  /** Stroke + gradient colour. Defaults to the brand series palette by order. */
  color?: string;
}

/** A horizontal reference line (e.g. a budget / target ceiling). */
export interface AreaTrendReferenceLine {
  /** Y value at which to draw the line. */
  y: number;
  /** Optional in-chart label (e.g. "Budget"). */
  label?: string;
  /** Line + label colour. Defaults to the warning token. */
  color?: string;
}

export interface AreaTrendProps {
  data: Array<Record<string, number | string>>;
  /** Key for the X (category/time) axis. */
  xKey: string;
  /** One or more area series. */
  series: AreaTrendSeries[];
  /** Optional horizontal reference lines (budget / target ceilings). */
  referenceLines?: AreaTrendReferenceLine[];
  /** Pixel height of the chart area. Default 240. */
  height?: number;
  /** Format Y-axis ticks + tooltip values. */
  valueFormatter?: (value: number | string | undefined) => string;
  showGrid?: boolean;
  showLegend?: boolean;
  /** Required: short text description for screen readers (chart is role="img"). */
  ariaLabel: string;
  className?: string;
}

export function AreaTrend({
  data,
  xKey,
  series,
  referenceLines,
  height = 240,
  valueFormatter,
  showGrid = true,
  showLegend = false,
  ariaLabel,
  className,
}: AreaTrendProps) {
  const reduced = usePrefersReducedMotion();
  const gradId = useId().replace(/:/g, '');
  const rtl = isDocRtl();

  return (
    <figure role="img" aria-label={ariaLabel} className={className} style={{ margin: 0, width: '100%' }}>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <defs>
            {series.map((s, i) => {
              const color = s.color ?? seriesColor(i);
              return (
                <linearGradient key={s.key} id={`${gradId}-${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.32} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.02} />
                </linearGradient>
              );
            })}
          </defs>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} vertical={false} />}
          <XAxis
            dataKey={xKey}
            reversed={rtl}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: CHART_COLORS.grid }}
          />
          <YAxis
            orientation={rtl ? 'right' : 'left'}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={valueFormatter}
          />
          <Tooltip
            cursor={{ stroke: CHART_COLORS.grid }}
            content={<ChartTooltip valueFormatter={valueFormatter} />}
          />
          {showLegend && <Legend wrapperStyle={{ fontSize: 12, color: CHART_COLORS.text }} />}
          {series.map((s, i) => {
            const color = s.color ?? seriesColor(i);
            return (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name ?? s.key}
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradId}-${i})`}
                isAnimationActive={!reduced}
                activeDot={{ r: 4 }}
                dot={false}
              />
            );
          })}
          {referenceLines?.map((rl, i) => {
            const color = rl.color ?? CHART_COLORS.warning ?? '#D97706';
            return (
              <ReferenceLine
                key={`ref-${i}`}
                y={rl.y}
                stroke={color}
                strokeDasharray="6 4"
                strokeWidth={1.5}
                ifOverflow="extendDomain"
                label={
                  rl.label
                    ? {
                        value: rl.label,
                        position: rtl ? 'insideLeft' : 'insideRight',
                        fill: color,
                        fontSize: 11,
                        fontWeight: 600,
                      }
                    : undefined
                }
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    </figure>
  );
}
